import { AxiosResponse } from "axios";
import { jwtDecode as jwt_decode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

import {
  LoginResponseBodyType,
  LoginResponseBodyUserType,
} from "@intelligems/ig-types";
import { GENERAL_ROUTES } from "@src/routes/routes";
import { setUser } from "@src/utils/setUser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useQueryParam } from "@src/hooks/queryParams";
import { QueryMeta } from "../../types/react-query";
import { getAuthToken, Logout } from "../../utils";
import { IgStorage } from "../../utils/igStorage";
import { api } from "../api";
import { useGetOrg } from "../organizations/get-organization";
import { userKeys } from "../queryKeys";

const login = async (data: { email: string; password: string }) => {
  const resp = await api.post<LoginResponseBodyType>(`/v2/login`, { ...data });
  return resp.data;
};

export const useRefreshShopifyScopes = () => {
  const token = getAuthToken();
  const org = useGetOrg();

  return useMutation<LoginResponseBodyType, Error, any, any>({
    mutationFn: async () =>
      JwtLogin({ token: token!, orgId: org.data.org.id, reAuth: true }),
    onSuccess(data) {
      if (data.type === "redirect") {
        open(data.redirect, "_blank");
      }
    },
  });
};

export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const widgetAuth = useQueryParam("widgetAuth");

  return useMutation<LoginResponseBodyType, Error, any, any>({
    mutationFn: async (data: { email: string; password: string }) =>
      login(data),
    onSuccess: (data) => {
      if (data.type === "redirect") {
        void navigate(data.redirect);
      } else if (data.type === "clear") {
        IgStorage.clearAuthTokens();
        window.location.reload();
      } else {
        if (
          window.location.pathname === "/launch" &&
          !data.organization.installed
        ) {
          IgStorage.clearAuthTokens();
        } else if (widgetAuth) {
          try {
            // eslint-disable-next-line react-compiler/react-compiler
            IgStorage.currentToken = data.token;
          } catch (e) {
            console.warn(e);
          }
        } else {
          IgStorage.authToken = data.token;
          queryClient.setQueryData(userKeys.all, data);
          setUser(data);
        }
      }
    },
  });
};

let pendingLogin: {
  promise: Promise<LoginResponseBodyType>;
  params: string;
} | null = null;

export const JwtLogin = async (options: {
  token: string;
  orgId?: string;
  shopId?: string;
  reAuth?: boolean;
  returnUrl?: string;
}): Promise<LoginResponseBodyType> => {
  const paramKey = JSON.stringify(options);

  if (pendingLogin && pendingLogin.params === paramKey) {
    return pendingLogin.promise;
  }

  const promise = (async () => {
    try {
      let decodedOrgId: string | undefined;
      if (options.orgId) {
        try {
          const decodedToken = jwt_decode<{ orgId?: string }>(options.token);
          decodedOrgId = decodedToken.orgId;
        } catch {
          // Token isn't a decodable JWT (e.g. ShareToken or corrupt storage).
          // Fall through to the API call with full params; the server will
          // reject invalid tokens.
        }
      }
      let resp: AxiosResponse<LoginResponseBodyType>;

      if (!options.orgId || decodedOrgId === options.orgId) {
        resp = await api.post<LoginResponseBodyType>("/v2/login", {
          token: options.token,
          reAuth: options.reAuth,
          returnUrl: options.returnUrl,
        });
      } else {
        resp = await api.post<LoginResponseBodyType>("/v2/login", {
          token: options.token,
          orgId: options.orgId,
          shopId: options.shopId,
          reAuth: options.reAuth,
          returnUrl: options.returnUrl,
        });
      }

      return resp.data;
    } finally {
      // Clear pending if this was the current request
      if (pendingLogin?.params === paramKey) {
        pendingLogin = null;
      }
    }
  })();

  pendingLogin = { promise, params: paramKey };
  return promise;
};

export const useLoggedInUser = () => {
  const urlJWT = getAuthToken();
  return useQuery<LoginResponseBodyType, Error, LoginResponseBodyUserType, any>(
    {
      queryKey: userKeys.all,
      queryFn: async () =>
        JwtLogin({
          token: urlJWT!,
        }),
      enabled: window.location.pathname !== "/launch" && !!urlJWT,
    }
  );
};

export const useUser = (options?: {
  enabled?: boolean;
  refetchInterval?: number;
}) => {
  const searchParams = new URLSearchParams(window.location.search);
  const queryClient = useQueryClient();
  let isLaunchWithWrongOrg = false;
  const widgetAuth = searchParams.get("widgetAuth");
  // org id is out of sync with token, so the other store is requested
  const urlJWT = getAuthToken();
  let orgId: string | undefined = undefined;
  if (urlJWT) {
    try {
      const decoded = jwt_decode<any>(urlJWT);
      orgId = decoded.orgId;
      if (
        window.location.pathname === "/launch" &&
        decoded.shopId !== searchParams.get("shop")
      ) {
        isLaunchWithWrongOrg = true;
      }
    } catch {
      // Non-JWT token (e.g. ShareToken) or corrupt storage — skip decoding.
    }
  }
  const url = new URL(window.location.href);
  const returnUrl = url.searchParams.get("returnUrl") ?? url.pathname;

  return useQuery<LoginResponseBodyType, Error, LoginResponseBodyType, any>({
    queryKey: userKeys.all,
    queryFn: async () =>
      JwtLogin({
        token: urlJWT!,
        orgId: widgetAuth || orgId || undefined,
        shopId:
          searchParams.get("shop") || searchParams.get("shopId") || undefined,
        returnUrl,
      }),
    enabled:
      !isLaunchWithWrongOrg &&
      !!urlJWT &&
      urlJWT !== "undefined" &&
      (options?.enabled === undefined || options?.enabled === true),
    refetchInterval: options?.refetchInterval,
    retry: 3,
    meta: {
      onSuccess: (data: LoginResponseBodyType) => {
        if (data.type === "redirect") {
          // eslint-disable-next-line react-compiler/react-compiler
          window.location.href = data.redirect + "&src=useUserOnSuccess";
        } else if (data.type === "clear") {
          IgStorage.clearAuthTokens();
          window.location.reload();
        } else {
          if (window.location.pathname === "/launch") {
            if (data.organization.installed) {
              window.location.pathname = GENERAL_ROUTES.Index;
            }
          } else {
            IgStorage.authToken = data.token;
            setUser(data);
          }
        }
      },
      onError: {
        any: () => {
          if (!searchParams.has("widgetAuth")) {
            queryClient.removeQueries({ queryKey: userKeys.all });
            Logout();
          }
        },
      },
    } as QueryMeta,
  });
};
